// src/ui/PdpaRegister.tsx – strictly admin only access
// Compliance register for PDPA storage-notice opt-outs: current status per
// clinic, clinic confirmations (who/when/attestation), full event history and
// CSV export, to support evidence gathering during a PDPC control.
import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import {
  buildPdpaRegisterCsv,
  formatPdpaNoticeStatusLabel,
  getPdpaNoticeStatus,
} from "../lib/pdpaOptout.mjs";

interface RegisterClient {
  id: string;
  raison_sociale: string;
  pdpa_consent_message: string | null;
  pdpa_consent_messages_enabled: boolean;
  pdpa_optout_requested_at: string | null;
  pdpa_optout_confirmed_at: string | null;
  pdpa_optout_confirmed_by: string | null;
  pdpa_optout_confirmed_by_name: string | null;
  pdpa_optout_attestation: string | null;
}

interface RegisterEvent {
  id: number;
  client_id: string;
  action: string;
  actor_id: string;
  actor_role: string;
  actor_name: string | null;
  attestation: string | null;
  created_at: string;
}

const ACTION_LABELS: Record<string, string> = {
  messages_disabled: "Notices disabled",
  messages_enabled: "Notices re-enabled",
  clinic_confirmed: "Clinic confirmed opt-out",
};

const STATUS_BADGE_CLASSES: Record<string, string> = {
  active: "bg-sky-100 text-sky-700",
  disabled_pending_confirmation: "bg-amber-100 text-amber-700",
  disabled_confirmed: "bg-green-100 text-green-700",
};

function formatDate(value: string | null | undefined) {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-SG", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function PdpaRegister() {
  const [clients, setClients] = useState<RegisterClient[]>([]);
  const [events, setEvents] = useState<RegisterEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [clientsRes, eventsRes] = await Promise.all([
        supabase
          .from("clients")
          .select(
            "id, raison_sociale, pdpa_consent_message, pdpa_consent_messages_enabled, " +
              "pdpa_optout_requested_at, pdpa_optout_confirmed_at, pdpa_optout_confirmed_by, " +
              "pdpa_optout_confirmed_by_name, pdpa_optout_attestation"
          )
          .order("raison_sociale", { ascending: true }),
        supabase
          .from("pdpa_optout_events")
          .select("*")
          .order("created_at", { ascending: false }),
      ]);

      if (clientsRes.error) {
        setError(clientsRes.error.message);
      } else {
        setClients((clientsRes.data || []) as unknown as RegisterClient[]);
      }
      if (eventsRes.error) {
        setError((previous) => previous || eventsRes.error!.message);
      } else {
        setEvents((eventsRes.data || []) as unknown as RegisterEvent[]);
      }
      setLoading(false);
    })();
  }, []);

  const downloadCsv = () => {
    const csv = buildPdpaRegisterCsv(clients);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `pdpa-register-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const optedOut = clients.filter((c) => c.pdpa_consent_messages_enabled === false);
  const pending = optedOut.filter((c) => !c.pdpa_optout_confirmed_at);

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 font-sans text-gray-800">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-primary">🔐 PDPA register</h1>
        <button
          onClick={downloadCsv}
          className="rounded bg-primary px-4 py-2 text-sm text-white shadow hover:bg-opacity-90"
        >
          Export CSV
        </button>
      </div>

      <p className="mb-4 text-sm text-gray-600">
        Register of PDPA storage-notice opt-outs. For each clinic that disabled the automatic
        notices, this page records who requested it, when, and the clinic&apos;s signed attestation
        that it informs patients by its own means. Events are insert-only and cannot be edited.
      </p>

      {pending.length > 0 && (
        <div className="mb-4 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          ⚠️ {pending.length} clinic(s) have notices disabled without a clinic confirmation yet:{" "}
          {pending.map((c) => c.raison_sociale).join(", ")}. Chase these confirmations — they are
          your evidence in case of a PDPC control.
        </div>
      )}

      {loading && <p className="text-gray-500">Loading…</p>}
      {error && <p className="text-red-600">Error: {error}</p>}

      {!loading && !error && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-slate-50 text-left text-xs uppercase text-gray-500">
                <th className="px-4 py-3">Clinic</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Disabled on</th>
                <th className="px-4 py-3">Confirmed by</th>
                <th className="px-4 py-3">Confirmed on</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => {
                const status = getPdpaNoticeStatus(c);
                const clientEvents = events.filter((e) => e.client_id === c.id);
                const expanded = expandedId === c.id;
                return (
                  <>
                    <tr key={c.id} className="border-b last:border-0">
                      <td className="px-4 py-3 font-medium">{c.raison_sociale}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                            STATUS_BADGE_CLASSES[status] || STATUS_BADGE_CLASSES.active
                          }`}
                        >
                          {formatPdpaNoticeStatusLabel(status)}
                        </span>
                      </td>
                      <td className="px-4 py-3">{formatDate(c.pdpa_optout_requested_at)}</td>
                      <td className="px-4 py-3">
                        {c.pdpa_optout_confirmed_by_name || c.pdpa_optout_confirmed_by || "—"}
                      </td>
                      <td className="px-4 py-3">{formatDate(c.pdpa_optout_confirmed_at)}</td>
                      <td className="px-4 py-3 text-right">
                        {(clientEvents.length > 0 || c.pdpa_optout_attestation) && (
                          <button
                            onClick={() => setExpandedId(expanded ? null : c.id)}
                            className="text-blue-600 hover:underline"
                          >
                            {expanded ? "Hide history" : "History"}
                          </button>
                        )}
                      </td>
                    </tr>
                    {expanded && (
                      <tr key={`${c.id}-history`} className="border-b bg-slate-50 last:border-0">
                        <td colSpan={6} className="px-4 py-3">
                          {c.pdpa_optout_attestation && (
                            <p className="mb-3 rounded border border-gray-200 bg-white p-3 text-xs italic text-gray-700">
                              {c.pdpa_optout_attestation}
                            </p>
                          )}
                          {clientEvents.length === 0 ? (
                            <p className="text-xs text-gray-500">No recorded events.</p>
                          ) : (
                            <ul className="space-y-1 text-xs text-gray-700">
                              {clientEvents.map((e) => (
                                <li key={e.id}>
                                  <span className="font-medium">
                                    {ACTION_LABELS[e.action] || e.action}
                                  </span>{" "}
                                  — {formatDate(e.created_at)} — {e.actor_role}:{" "}
                                  {e.actor_name || e.actor_id}
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
